import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { fetchJSON } from '../utils/api';
import { hintOf } from '../utils/uploadKinds';

// Professional qualification declaration — submission form
// (docs/modules/workflow-ld-pq.md). Always for the logged-in user; the backend
// resolves the staff link from the login email (identity rule: email only).
// The qualification is picked from the catalogue (L&D Records → Qualification
// Types); on approval the formal record is created on the staff member.

// Client-side required check → the empty fields as `[{ key, label }]` for the
// submit-time marking (formValidation); the server re-validates. ONE predicate
// for BOTH surfaces — this form and the Modify modal on the detail page import
// the same rules, so the two cannot drift apart. `needsExpiry` comes from the
// caller because only the caller knows which catalogue row is selected. The
// keys are the `field=` keys the detail page's return comments anchor to.
export const pqRequiredGaps = ({ qualificationTypeId, awardedDate, expiryDate, needsExpiry }) => {
  const gaps = [];
  if (!qualificationTypeId) gaps.push({ key: 'qualificationTitle', label: 'Qualification' });
  if (!awardedDate) gaps.push({ key: 'awardedDate', label: 'Awarded On' });
  if (needsExpiry && !expiryDate) gaps.push({ key: 'expiryDate', label: 'Expires On' });
  return gaps;
};

// The one rule an empty box cannot express — checked once the gaps are filled.
export const pqDateOrderError = ({ awardedDate, expiryDate, needsExpiry }) => (
  needsExpiry && expiryDate && awardedDate && expiryDate < awardedDate
    ? 'The expiry date cannot be earlier than the awarded date.'
    : null
);

const WorkflowPqNew = () => {
  const navigate = useNavigate();

  const [types, setTypes] = useState([]);
  const [typesError, setTypesError] = useState(null);
  const [qualificationTypeId, setQualificationTypeId] = useState('');
  const [awardedDate, setAwardedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    fetchJSON('/api/ld/qualification-types/options')
      .then((d) => { if (active) setTypes(d.data || []); })
      .catch((e) => { if (active) setTypesError(e.message); });
    return () => { active = false; };
  }, []);

  const selectedType = types.find(t => String(t.id) === String(qualificationTypeId)) || null;
  // Qualifications the catalogue marks for renewal lapse, so the date
  // they run out is part of the declaration. Everything else never shows the
  // field — an expiry date on a lifelong qualification is a wrong answer.
  const needsExpiry = !!selectedType?.requiresRenewal;

  const gaps = useMemo(
    () => pqRequiredGaps({ qualificationTypeId, awardedDate, expiryDate, needsExpiry }),
    [qualificationTypeId, awardedDate, expiryDate, needsExpiry],
  );
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    const problem = pqDateOrderError({ awardedDate, expiryDate, needsExpiry });
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('qualificationTypeId', qualificationTypeId);
      form.set('awardedDate', awardedDate);
      // Not a renewable qualification → no expiry, whatever an earlier pick left behind.
      if (needsExpiry && expiryDate) form.set('expiryDate', expiryDate);
      if (remarks.trim()) form.set('remarks', remarks.trim());
      files.forEach(f => form.append('files', f));

      const data = await fetchJSON('/api/ld/pq-requests', { method: 'POST', body: form });
      navigate(`/workflow/pq/${data.request.id}`);
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/pq"
      backLabel="Professional Qualification"
      title="New Professional Qualification Declaration"
      subtitle="Declare a professional qualification you hold."
      error={error || validation.error}
      onSubmit={submit}
      submitLabel="Submit"
      submitting={saving}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Qualification">
          <Field
            label="Qualification"
            required
            field="qualificationTitle"
            hint={<>Pick from the firm's list. <InfoTip text="If yours is missing, ask HR to add it under L&D Records → Qualification Types first." /></>}
          >
            <select
              value={qualificationTypeId}
              onChange={e => setQualificationTypeId(e.target.value)}
              className={inputCls}
            >
              <option value="">Select…</option>
              {types.map(t => (
                <option key={t.id} value={t.id}>{t.fullName ? `${t.title} — ${t.fullName}` : t.title}</option>
              ))}
            </select>
            {/* Awarding body / jurisdiction of the picked row —
                confirmation that this
                is the right one before submitting. */}
            {selectedType && (selectedType.awardingBody || selectedType.jurisdiction) && (
              <p className="mt-1 text-xs text-slate-500">
                {[selectedType.awardingBody, selectedType.jurisdiction].filter(Boolean).join(' · ')}
              </p>
            )}
            {typesError && <p className="mt-1 text-xs text-red-600">{typesError}</p>}
            {!typesError && types.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">No qualifications on the list yet — ask HR to add them.</p>
            )}
          </Field>
          <Field label="Awarded On" required field="awardedDate" hint="The date on the certificate / admission letter.">
            <input
              type="date"
              value={awardedDate}
              onChange={e => setAwardedDate(e.target.value)}
              className={inputCls}
            />
          </Field>
          {needsExpiry && (
            <Field
              label="Expires On"
              required
              field="expiryDate"
              hint="This qualification has to be renewed — give the date the current certificate / membership runs out."
            >
              <input
                type="date"
                value={expiryDate}
                min={awardedDate || undefined}
                onChange={e => setExpiryDate(e.target.value)}
                className={inputCls}
              />
            </Field>
          )}
        </FormSection>

        <FormSection step={2} title="Certificate">
          <Field
            label="Upload Certificate"
            hint={`Recommended — ${hintOf('docsAndImages', { multiple: true })} 10 files at a time.`}
          >
            <FilePicker
              multiple
              kind="docsAndImages"
              disabled={saving}
              onChange={e => setFiles(Array.from(e.target.files || []))}
              files={files}
            />
          </Field>
        </FormSection>

        <FormSection step={3} title="Remarks">
          <Field label="Remarks">
            <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowPqNew;
